/* React */
import React, { useEffect, useRef } from "react";

/* React Router */
import {
  Redirect,
  Route,
  useRouteMatch,
} from "react-router";

/* React Redux */
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";
import { setCurrentDisease } from "src/redux/features/custom-cohorts/customCohortsSlice";
import { setMenuType } from "src/redux/features/menu/secondary-menu/secondaryMenuSlice";
import { fetchUserRoles } from "src/redux/thunks/auth/userRolesThunk";
import { setToastMessage } from "src/redux/features/toast-message/toastMessageSlice";
import { fetchAllowedCareSites } from "src/redux/thunks/auth/allowedCareSitesThunk";
import { fetchBackendSettings } from "src/redux/thunks/settings/backendSettingsThunk";
/* Ionic */
import { IonRouterOutlet } from "@ionic/react";

/* Thunks */
import { syncMainCondition } from "src/redux/thunks/insights-filter/main-condition/syncMainConditionThunk";

/* Utils */
import PageWrapper from "src/utils/wrapper/page-wrapper/PageWrapper";
import SecondaryMenuWrapper from "src/utils/wrapper/secondary-menu-wrapper/SecondaryMenuWrapper";
import InsightsPageWrapper from "src/utils/wrapper/insights-page-wrapper/InsightsPageWrapper";

/* Components */
import LoadingScreen from "src/components/shared/loading-screen/LoadingScreen";
import CustomCohorts from "src/components/insights/custom-cohorts/CustomCohorts";

/* Pages */
import DescriptiveStatistics from "src/pages/insights/descriptive-statistics/DescriptiveStatistics";
import SurvivalAnalysis from "src/pages/insights/survival-analysis/SurvivalAnalysis";
import TreatmentPatterns from "src/pages/insights/treatment-patterns/TreatmentPatterns";

/* Constants */
import { Paths } from "src/shared/constants/common/Paths";
import { ErrorMessage } from "src/shared/constants/common/ErrorMessage";
import { GlobalMessageType } from "src/shared/constants/common/GlobalMessage";

import {
  MenuContentType,
  MenuType,
} from "src/shared/constants/menu/Menu";

const Insights: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const path = useRouteMatch();

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.data?.isAuthenticated ?? false
  );
  const allowedCareSitesStatus = useSelector(
    (state: RootState) => state.auth.allowedCareSitesStatus
  );
  const allowedCareSites = useSelector(
    (state: RootState) => state.auth.allowedCareSites
  );
  const {
    careSiteRestrictionEnabled,
    status: backendSettingsStatus,
  } = useSelector(
    (state: RootState) => state.settings.backendSettings
  );

  const hasFetchedAuthData = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      hasFetchedAuthData.current = false;
      return;
    }
    if (hasFetchedAuthData.current) return;
    hasFetchedAuthData.current = true;
    dispatch(fetchUserRoles());
    dispatch(fetchAllowedCareSites());
  }, [dispatch, isAuthenticated]);

  const secondaryMenuState = useSelector(
    (state: RootState) => state.secondaryMenu
  );

  const { menuType } = secondaryMenuState;

  useEffect(() => {
    /**
     * 'menuType' is set to 'MenuType.ADMIN_DASHBOARD'.
     * To ensure a consistent behavior, check if
     * 'menuType' is not the current menu before dispatching it.
     *
     * 'path' is required in dependency array.
     */

    if (menuType !== MenuType.INSIGHTS_FILTER) {
      dispatch(setMenuType(MenuType.INSIGHTS_FILTER));
    }
  }, [dispatch, path, menuType]);

  const insightsFilterState = useSelector(
    (state: RootState) => state.insightsFilter
  );

  const { mainCondition } = insightsFilterState;

  useEffect(() => {
    dispatch(syncMainCondition({ flush: false }));
  }, [dispatch, menuType]);

  /**
   * It is required to set the persistent state 'customCohorts'
   * explicitely in order to detect when the 'mainCondition'
   * is changing.
   */
  useEffect(() => {
    if (!mainCondition.value) return;
    dispatch(setCurrentDisease(mainCondition.value));
  }, [dispatch, mainCondition.value]);

  useEffect(() => {
    if (
      careSiteRestrictionEnabled &&
      allowedCareSitesStatus === "success" &&
      allowedCareSites?.length === 0
    ) {
      dispatch(
        setToastMessage({
          type: GlobalMessageType.WARNING,
          message: ErrorMessage.NO_CARE_SITE_ACCESS,
          show: true,
        })
      );
    }
  }, [
    dispatch,
    careSiteRestrictionEnabled,
    allowedCareSitesStatus,
    allowedCareSites,
  ]);

  return (
    <PageWrapper>
      <LoadingScreen />
      <CustomCohorts />
      {backendSettingsStatus === "error" ? (
        <div className="care-site-error" data-testid="backend-settings-error">
          Failed to load backend settings.{" "}
          <button onClick={() => dispatch(fetchBackendSettings())}>
            Retry
          </button>
        </div>
      ) : backendSettingsStatus !== "success" ? null : careSiteRestrictionEnabled &&
        allowedCareSitesStatus === "error" ? (
        <div className="care-site-error" data-testid="care-site-permissions-error">
          Failed to load care-site permissions.{" "}
          <button
            onClick={() => dispatch(fetchAllowedCareSites())}
          >
            Retry
          </button>
        </div>
      ) : careSiteRestrictionEnabled &&
        allowedCareSitesStatus === "success" &&
        allowedCareSites?.length === 0 ? null : !careSiteRestrictionEnabled ||
        allowedCareSitesStatus === "success" ? (
        <div data-testid="insights-routes">
          {menuType === MenuType.INSIGHTS_FILTER ? (
            <SecondaryMenuWrapper menuId={menuType} />
          ) : undefined}
          <IonRouterOutlet id={MenuContentType.INSIGHTS_FILTER}>
            <InsightsPageWrapper>
              <Route
                exact
                path={`${path.url}${Paths.DESCRIPTIVE_STATISTICS}`}
                component={DescriptiveStatistics}
              />
              <Route
                exact
                path={`${path.url}${Paths.SURVIVAL_ANALYSIS}`}
                component={SurvivalAnalysis}
              />
              <Route
                exact
                path={`${path.url}${Paths.TREATMENT_PATTERNS}`}
                component={TreatmentPatterns}
              />
              <Route exact path={`${path.url}`}>
                <Redirect
                  to={`${path.url}${Paths.DESCRIPTIVE_STATISTICS}`}
                />
              </Route>
              <Route
                path={`${path.url}/:unknown`}
                render={({ match }) => {
                  const validPaths = [
                    Paths.DESCRIPTIVE_STATISTICS,
                    Paths.SURVIVAL_ANALYSIS,
                    Paths.TREATMENT_PATTERNS,
                    Paths.ADMIN_DASHBOARD,
                  ];

                  const isValid = validPaths.includes(
                    match.params.unknown as Paths
                  );

                  return isValid ? null : (
                    <Redirect
                      to={`${path.url}${Paths.DESCRIPTIVE_STATISTICS}`}
                    />
                  );
                }}
              />
            </InsightsPageWrapper>
          </IonRouterOutlet>
        </div>
      ) : null}
    </PageWrapper>
  );
};

export default Insights;
